// Privacy Policy Page
// Publicly accessible page for Twilio compliance and user transparency

import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, ArrowLeft, Shield, Mail, Phone } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const lastUpdated = "April 20, 2026";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              IntakePal
            </span>
          </Link>
          <Link 
            to="/" 
            className="text-sm text-slate-600 hover:text-teal-600 flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-12">
          {/* Title */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Privacy Policy
              </h1>
              <p className="text-slate-500 text-sm">Last updated: {lastUpdated}</p>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-slate max-w-none">
            <p className="text-lg text-slate-600 mb-8">
              IntakePal (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our client intake automation platform.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">Information You Provide</h3>
            <p className="text-slate-600 mb-4">We collect information you voluntarily provide when using our services, including:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li><strong>Account Information:</strong> Name, email address, phone number, law firm name, and password when you register for an account.</li>
              <li><strong>Client Intake Data:</strong> Information submitted through intake forms, including client names, contact information, accident details, injury descriptions, and uploaded documents (photos, medical records, police reports).</li>
              <li><strong>Payment Information:</strong> Billing details processed securely through our payment processor, Stripe.</li>
              <li><strong>Communications:</strong> Messages you send to us or through our platform.</li>
            </ul>

            <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">Information Collected Automatically</h3>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the platform.</li>
              <li><strong>Device Information:</strong> Browser type, operating system, IP address.</li>
              <li><strong>Cookies:</strong> We use cookies to maintain sessions and improve user experience.</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">2. How We Use Your Information</h2>
            <p className="text-slate-600 mb-4">We use the collected information to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Provide, maintain, and improve our services</li>
              <li>Process client intake submissions and generate case summaries</li>
              <li>Send automated follow-up communications via email and SMS</li>
              <li>Send notifications about new leads and account activity</li>
              <li>Process payments and manage subscriptions</li>
              <li>Respond to customer support inquiries</li>
              <li>Analyze usage patterns to improve our platform</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">3. SMS and Email Communications</h2>
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-medium text-teal-900 mb-3">Important Notice About Messaging</h3>
              <p className="text-teal-800 mb-4">
                When potential clients submit an intake form, they may consent to receive follow-up communications from the law firm using IntakePal, including:
              </p>
              <ul className="list-disc pl-6 text-teal-800 space-y-2 mb-4">
                <li>Automated SMS text messages regarding their inquiry</li>
                <li>Email communications about their case</li>
                <li>Appointment reminders and status updates</li>
              </ul>
              <p className="text-teal-800 mb-2"><strong>Message Frequency:</strong> You will receive up to 4 automated messages per inquiry.</p>
              <p className="text-teal-800 mb-2"><strong>Opt-Out:</strong> You can reply STOP to any SMS message to unsubscribe at any time. You may also text HELP for assistance.</p>
              <p className="text-teal-800 mb-2"><strong>Message and data rates may apply.</strong> Please contact your mobile carrier for details.</p>
              <p className="text-teal-800 mt-4 font-semibold">We do not share, sell, or transfer your mobile phone number to third parties or affiliates for marketing or promotional purposes.</p>
            </div>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">4. Information Sharing and Disclosure</h2>
            <p className="text-slate-600 mb-4">We do not sell your personal information. <strong>We do not share, sell, or transfer mobile phone numbers to third parties or affiliates for marketing or promotional purposes.</strong> We may share information with:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li><strong>Law Firms (Our Customers):</strong> Client intake data is shared with the law firm whose intake form was submitted.</li>
              <li><strong>Service Providers:</strong> Third-party vendors who assist in operating our platform (e.g., Stripe for payments, Twilio for SMS, SendGrid for email).</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">5. Data Security</h2>
            <p className="text-slate-600 mb-4">
              We implement industry-standard security measures to protect your information, including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Secure password hashing</li>
              <li>Regular security assessments</li>
              <li>Access controls and authentication</li>
            </ul>
            <p className="text-slate-600 mb-4">
              However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">6. Data Retention</h2>
            <p className="text-slate-600 mb-4">
              We retain your information according to the following schedule:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li><strong>Account Information:</strong> Retained for as long as your account is active, plus 7 years after account closure for legal and tax compliance.</li>
              <li><strong>Client Intake Data:</strong> Retained according to the law firm&apos;s configured preferences. Default retention is 7 years from intake date, in accordance with typical legal record retention requirements.</li>
              <li><strong>Communication Logs:</strong> SMS and email notification logs are retained for 3 years.</li>
              <li><strong>Payment Records:</strong> Transaction records are retained for 7 years for tax and accounting purposes.</li>
              <li><strong>Usage Analytics:</strong> Anonymized usage data may be retained indefinitely for service improvement.</li>
            </ul>
            <p className="text-slate-600 mb-4">
              Law firms using our platform may configure custom retention periods based on their jurisdiction&apos;s requirements. Upon request, data can be deleted earlier unless retention is required by law.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">7. Your Rights and Choices</h2>
            <p className="text-slate-600 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li><strong>Access:</strong> Request a copy of your personal information in a portable format.</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information.</li>
              <li><strong>Deletion:</strong> Request deletion of your account and associated data (subject to legal retention requirements).</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications.</li>
              <li><strong>SMS Opt-Out:</strong> Reply STOP to any text message to immediately stop receiving texts.</li>
              <li><strong>Do Not Sell:</strong> We do not sell your personal information. California residents may still submit a &quot;Do Not Sell&quot; request.</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3">How to Exercise Your Rights</h3>
            <p className="text-slate-600 mb-4">
              To submit a data access, correction, or deletion request:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Email us at <strong>privacy@intakepal.com</strong> with the subject line &quot;Data Request&quot;</li>
              <li>Include your full name, email address, and the specific request (access, correction, or deletion)</li>
              <li>We will verify your identity and respond within 30 days (45 days for complex requests)</li>
            </ul>
            <p className="text-slate-600 mb-4">
              For law firm customers managing client data, please contact us to initiate a data deletion request for specific intake records.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">8. SMS/Text Message Compliance (TCPA)</h2>
            <p className="text-slate-600 mb-4">
              Our platform enables law firms to send text messages to potential clients. We comply with the Telephone Consumer Protection Act (TCPA) and require:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li><strong>Express Consent:</strong> Text messages are only sent to individuals who have provided explicit consent through our intake process.</li>
              <li><strong>Clear Disclosure:</strong> Users are informed about text messaging before providing their phone number.</li>
              <li><strong>Easy Opt-Out:</strong> Recipients can reply STOP at any time to immediately stop receiving messages.</li>
              <li><strong>Message Frequency:</strong> Message frequency varies based on case status and law firm preferences.</li>
              <li><strong>No Automated Marketing:</strong> Text messages are limited to case-related communications, not marketing.</li>
            </ul>
            <p className="text-slate-600 mb-4">
              Message and data rates may apply. Carriers are not liable for delayed or undelivered messages.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">9. California Privacy Rights (CCPA/CPRA)</h2>
            <p className="text-slate-600 mb-4">
              California residents have additional rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA):
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li><strong>Right to Know:</strong> Request what personal information we collect, use, and disclose.</li>
              <li><strong>Right to Delete:</strong> Request deletion of your personal information.</li>
              <li><strong>Right to Correct:</strong> Request correction of inaccurate personal information.</li>
              <li><strong>Right to Opt-Out of Sale:</strong> We do not sell personal information.</li>
              <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your rights.</li>
            </ul>
            <p className="text-slate-600 mb-4">
              To submit a request, email <strong>privacy@intakepal.com</strong> or call (555) 123-4567. We will respond within 45 days.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">10. Children&apos;s Privacy</h2>
            <p className="text-slate-600 mb-4">
              Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">11. Changes to This Policy</h2>
            <p className="text-slate-600 mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the &quot;Last updated&quot; date.
            </p>

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">12. Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have questions about this Privacy Policy or our practices, please contact us:
            </p>
            <div className="bg-slate-50 rounded-xl p-6">
              <p className="text-slate-700 mb-2"><strong>IntakePal</strong></p>
              <p className="text-slate-600 flex items-center gap-2 mb-1">
                <Mail className="w-4 h-4" /> support@intakepal.com
              </p>
              <p className="text-slate-600 flex items-center gap-2 mb-1">
                <Mail className="w-4 h-4" /> privacy@intakepal.com (Privacy requests)
              </p>
              <p className="text-slate-600 flex items-center gap-2">
                <Phone className="w-4 h-4" /> (555) 123-4567
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-slate-500">
          <p>&copy; {new Date().getFullYear()} IntakePal. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <Link to="/privacy" className="hover:text-teal-600">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-teal-600">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
